import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; username: string; password: string; full_name?: string; role?: string }) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 检查本地存储中的token和用户信息
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      
      if (response.success && response.data) {
        const { token: newToken, user: newUser } = response.data;
        
        // 保存到本地存储
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(newUser));
        
        // 更新状态
        setToken(newToken);
        setUser(newUser);
      } else {
        throw new Error(response.message || '登录失败');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || '登录失败');
    }
  };

  const register = async (data: { email: string; username: string; password: string; full_name?: string; role?: string }) => {
    try {
      const response = await authAPI.register({
        ...data,
        role: (data.role as 'student' | 'teacher') || 'student'
      });
      
      if (response.success && response.data) {
        const { token: newToken, user: newUser } = response.data;
        
        // 保存到本地存储
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(newUser));
        
        // 更新状态
        setToken(newToken);
        setUser(newUser);
      } else {
        throw new Error(response.message || '注册失败');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || '注册失败');
    }
  };

  const logout = () => {
    // 清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // 清除状态
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      const response = await authAPI.updateProfile(data);
      
      if (response.success && response.data) {
        const updatedUser = response.data;
        
        // 更新本地存储
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // 更新状态
        setUser(updatedUser);
      } else {
        throw new Error(response.message || '更新失败');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || '更新失败');
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    updateProfile,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
